import {
  AiBrain01Icon,
  ChartLineData02Icon,
  Dumbbell02Icon,
  GiftIcon,
  Home05Icon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  RankingIcon,
  Settings01Icon,
  UserCircleIcon,
} from '@hugeicons/core-free-icons';
import { NavLink, useLocation } from 'react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useChatContext } from '@/contexts/ChatContext';
import { hapticSelection } from '@/lib/haptics';
import { preloadRouteComponent } from '@/lib/route-preloads';
import { cn } from '@/lib/utils';
import { AppHugeIcon, type HugeIconDefinition } from '@/components/ui/icon';
import { BrandWordmark } from '@/components/ui/brand-wordmark';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

type DesktopNavItem = {
  id: string;
  labelKey: string;
  icon: HugeIconDefinition;
  path?: string;
  action?: 'chat';
};

const PRIMARY_NAV_ITEMS: DesktopNavItem[] = [
  {
    id: 'home',
    labelKey: 'navigation.home',
    icon: Home05Icon,
    path: '/dashboard',
  },
  {
    id: 'coach',
    labelKey: 'navigation.coach',
    icon: Dumbbell02Icon,
    path: '/coach',
  },
  {
    id: 'ai-chat',
    labelKey: 'navigation.aiCoach',
    icon: AiBrain01Icon,
    action: 'chat',
  },
  {
    id: 'performance',
    labelKey: 'navigation.performance',
    icon: ChartLineData02Icon,
    path: '/performance',
  },
] as const;

const SECONDARY_NAV_ITEMS: DesktopNavItem[] = [
  {
    id: 'rewards',
    labelKey: 'navigation.rewards',
    icon: GiftIcon,
    path: '/rewards',
  },
  {
    id: 'leaderboard',
    labelKey: 'navigation.leaderboard',
    icon: RankingIcon,
    path: '/leaderboard',
  },
  {
    id: 'profile',
    labelKey: 'navigation.profile',
    icon: UserCircleIcon,
    path: '/profile',
  },
] as const;

const ADMIN_NAV_ITEM: DesktopNavItem = {
  id: 'admin',
  labelKey: 'admin.title',
  icon: Settings01Icon,
  path: '/admin',
};

function isPathActive(pathname: string, item: DesktopNavItem, chatOpen: boolean) {
  if (item.action === 'chat') return chatOpen;
  if (!item.path) return false;
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

function DesktopNavControl({
  item,
  collapsed,
  active,
  label,
  onClick,
  onPreload,
}: {
  item: DesktopNavItem;
  collapsed: boolean;
  active: boolean;
  label: string;
  onClick?: () => void;
  onPreload?: () => void;
}) {
  const className = cn(
    'group isolate flex h-11 w-full items-center gap-3 overflow-hidden rounded-lg px-3 text-body font-semibold',
    'outline-none transition-[background,color,transform] duration-150 active:scale-[0.96] focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    active
      ? 'bg-foreground text-background shadow-none'
      : 'text-muted-foreground hover:bg-surface-secondary hover:text-foreground',
    collapsed && 'justify-center px-0',
  );

  const content = (
    <>
      <AppHugeIcon
        icon={item.icon}
        size="lg"
        stroke={active ? 'default' : 'fine'}
        className={cn(
          'relative z-[1]',
          active ? 'text-background' : 'text-muted-foreground group-hover:text-foreground',
        )}
      />
      {!collapsed ? <span className="relative z-[1] truncate">{label}</span> : null}
    </>
  );

  const control =
    item.action === 'chat' ? (
      <button
        type="button"
        aria-label={label}
        aria-current={active ? 'page' : undefined}
        data-testid={item.id === 'ai-chat' ? 'desktop-sidebar-ai-chat-button' : undefined}
        className={className}
        onClick={onClick}
      >
        {content}
      </button>
    ) : (
      <NavLink
        aria-label={label}
        aria-current={active ? 'page' : undefined}
        className={className}
        onClick={onClick}
        onMouseEnter={onPreload}
        onFocus={onPreload}
        to={item.path ?? '/dashboard'}
      >
        {content}
      </NavLink>
    );

  if (!collapsed) return control;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{control}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

export function AppDesktopSidebar({
  collapsed,
  onCollapsedChange,
}: {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const { isOpen, setIsOpen } = useChatContext();
  const preloadRoute = useMemo(
    () => (path: string) => {
      void preloadRouteComponent(path).catch(() => {
        // Silently fail preloading.
      });
    },
    [],
  );

  const toggleLabel = t('ui.toggleSidebar', 'Toggle sidebar');
  const openChat = () => {
    hapticSelection();
    setIsOpen(true);
  };

  const renderItem = (item: DesktopNavItem) => {
    const label = t(item.labelKey);
    const active = isPathActive(location.pathname, item, isOpen);
    const path = item.path;
    const onPreload = path
      ? () => {
          preloadRoute(path);
        }
      : undefined;

    return (
      <DesktopNavControl
        key={item.id}
        item={item}
        collapsed={collapsed}
        active={active}
        label={label}
        onClick={item.action === 'chat' ? openChat : hapticSelection}
        onPreload={onPreload}
      />
    );
  };

  return (
    <aside
      className={cn(
        'app-desktop-sidebar fixed inset-y-0 left-0 z-40 hidden border-r border-border/70 bg-surface-primary md:flex',
        'flex-col transition-[width] duration-200',
      )}
      style={{ width: collapsed ? '5.25rem' : '15.25rem' }}
      aria-label={t('navigation.main', 'Main navigation')}
    >
      <div className="flex h-full min-h-0 flex-col px-3 py-4">
        <div className={cn('flex items-center gap-3 px-2', collapsed && 'justify-center px-0')}>
          <BrandWordmark compact={collapsed} />
        </div>

        <nav className="mt-8 flex flex-1 flex-col gap-1.5" aria-label={t('navigation.main')}>
          {PRIMARY_NAV_ITEMS.map(renderItem)}
          <div className="my-2 h-px bg-border/70" />
          {SECONDARY_NAV_ITEMS.map(renderItem)}
          {location.pathname.startsWith('/admin') ? renderItem(ADMIN_NAV_ITEM) : null}
        </nav>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={toggleLabel}
              className={cn(
                'flex h-11 w-full items-center gap-3 rounded-lg px-3 text-body font-semibold text-muted-foreground',
                'transition-[background,color,transform] duration-150 hover:bg-muted/50 hover:text-foreground active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                collapsed && 'justify-center px-0',
              )}
              onClick={() => onCollapsedChange(!collapsed)}
            >
              <AppHugeIcon
                icon={collapsed ? PanelLeftOpenIcon : PanelLeftCloseIcon}
                size="lg"
                stroke="fine"
              />
              {!collapsed ? <span className="truncate">{toggleLabel}</span> : null}
            </button>
          </TooltipTrigger>
          {collapsed ? <TooltipContent side="right">{toggleLabel}</TooltipContent> : null}
        </Tooltip>
      </div>
    </aside>
  );
}
